"use client";

// Windowed rendering for long message lists (CHAT-PERF): only the rows near the viewport are
// mounted; a spacer above and one below stand in for the rest, sized from measured heights
// (estimates until a row has been seen). The scroller (`MessageScroller`) keeps its own jobs:
// following the end as messages arrive (it watches the content's size, which the spacers keep
// honest) and the jump button.
//
// Anchoring is done here, because the scroller's prepend detection looks at its first child and
// that is now a spacer: after every render the first visible row is put back where it was on
// screen (by key, not index), unless the list sits at its end (then the scroller follows the
// end). That covers older pages loading above, rows above being measured for the first time,
// and a row above growing (an edit, a reaction).
import * as React from "react";

export type WindowRange = { start: number; end: number };

/** Prefix offsets: `offsets[i]` is the top of row i, `offsets[n]` the total height. */
export function offsetsOf(heights: readonly number[]): number[] {
  const out = new Array<number>(heights.length + 1);
  out[0] = 0;
  for (let i = 0; i < heights.length; i++) out[i + 1] = (out[i] as number) + (heights[i] as number);
  return out;
}

/**
 * The rows overlapping `[top - overscan, top + height + overscan]` (positions relative to the
 * first row). Never empty when there are rows.
 */
export function rangeFor(
  offsets: readonly number[],
  top: number,
  height: number,
  overscan: number,
): WindowRange {
  const n = offsets.length - 1;
  if (n <= 0) return { start: 0, end: 0 };
  const lo = top - overscan;
  const hi = top + height + overscan;
  // First row whose bottom is below `lo`; first row whose top is at or below `hi`.
  let start = 0;
  let a = 0;
  let b = n - 1;
  while (a <= b) {
    const mid = (a + b) >> 1;
    if ((offsets[mid + 1] as number) <= lo) a = mid + 1;
    else {
      start = mid;
      b = mid - 1;
    }
  }
  if ((offsets[n] as number) <= lo) start = n - 1;
  let end = start + 1;
  while (end < n && (offsets[end] as number) < hi) end++;
  return { start, end };
}

export type WindowedRowsOptions = {
  /** Stable keys, one per row, in order. */
  keys: readonly string[];
  /** A row's estimated height before it has been measured. */
  estimate: (index: number) => number;
  /** Off: every row renders (short lists). */
  enabled: boolean;
  /** The scrolling element. */
  viewport: React.RefObject<HTMLElement | null>;
  /** Pixels rendered beyond each edge of the viewport. */
  overscan?: number;
  /** Rows rendered at the end before the viewport has a size. */
  initialRows?: number;
};

export type WindowedRows = {
  range: WindowRange;
  /** Height of the spacer above the first rendered row, and below the last. */
  before: number;
  after: number;
  /** The rendered block (spacers and rows): its top is the rows' origin. */
  blockRef: React.RefCallback<HTMLElement>;
  /** Attach to each rendered row (with `data-row-key`): measures it. */
  measureRef: (key: string) => React.RefCallback<HTMLElement>;
  /**
   * CHAT-SWITCH-B: scrolls the viewport to a row by its key (its estimated place when it is not
   * rendered yet), so the window moves onto it and it mounts. False: no such row.
   */
  scrollToKey: (key: string) => boolean;
};

const AT_END_PX = 4;

export function useWindowedRows(o: WindowedRowsOptions): WindowedRows {
  const { keys, enabled, viewport } = o;
  const overscan = o.overscan ?? 600;
  const heights = React.useRef(new Map<string, number>());
  const [version, setVersion] = React.useState(0);
  const block = React.useRef<HTMLElement | null>(null);
  // The window by key, so rows inserted before it keep the same rows on screen.
  const [win, setWin] = React.useState<{ first: string; last: string; atEnd: boolean } | null>(
    null,
  );
  /** The row the reader is looking at: its key, its distance from the viewport's top. */
  const anchor = React.useRef<{ key: string; top: number; end: boolean } | null>(null);
  const observer = React.useRef<ResizeObserver | null>(null);
  const elements = React.useRef(new Map<Element, string>());

  const n = keys.length;
  // Offsets always from the latest measurements (rows are measured as they mount), so the
  // spacers, the window and the anchor agree; `version` re-renders when a row is resized.
  const live = React.useRef({ keys, estimate: o.estimate });
  live.current = { keys, estimate: o.estimate };
  const offsetsNow = React.useCallback(
    () =>
      offsetsOf(
        live.current.keys.map((k, i) => heights.current.get(k) ?? live.current.estimate(i)),
      ),
    [],
  );
  void version;
  const offsets = offsetsNow();
  const indexOf = React.useMemo(() => new Map(keys.map((k, i) => [k, i])), [keys]);

  let range: WindowRange;
  if (!enabled) range = { start: 0, end: n };
  else {
    const first = win ? indexOf.get(win.first) : undefined;
    const last = win ? indexOf.get(win.last) : undefined;
    const at = anchor.current ? indexOf.get(anchor.current.key) : undefined;
    if ((first === undefined || last === undefined) && at !== undefined && !anchor.current?.end) {
      // Windowing just started (the list grew past the threshold, e.g. an older page loaded)
      // with the reader somewhere in the middle: window around the row they were looking at.
      range = { start: Math.max(0, at - 10), end: Math.min(n, at + (o.initialRows ?? 40)) };
    } else if (first === undefined || last === undefined) {
      range = { start: Math.max(0, n - (o.initialRows ?? 40)), end: n };
    } else {
      // A window that reached the end keeps reaching it as rows arrive.
      const end = win?.atEnd ? n : last + 1;
      range = { start: Math.min(first, Math.max(0, end - 1)), end };
    }
  }

  const geometry = React.useCallback(() => {
    const vp = viewport.current;
    const el = block.current;
    if (!vp || !el) return null;
    const blockTop = el.getBoundingClientRect().top - vp.getBoundingClientRect().top + vp.scrollTop;
    return { vp, blockTop };
  }, [viewport]);

  /**
   * Records the anchor from the DOM, windowed or not: the first row (`data-row-key`) whose
   * bottom is below the viewport's top, and how far from that top it sits.
   */
  const record = React.useCallback(() => {
    const vp = viewport.current;
    if (!vp || vp.clientHeight === 0) return;
    const vpTop = vp.getBoundingClientRect().top;
    const end = vp.scrollHeight - vp.scrollTop - vp.clientHeight <= AT_END_PX;
    for (const el of vp.querySelectorAll<HTMLElement>("[data-row-key]")) {
      const r = el.getBoundingClientRect();
      if (r.bottom <= vpTop) continue;
      anchor.current = { key: el.dataset.rowKey ?? "", top: r.top - vpTop, end };
      return;
    }
    // Only a spacer in view (a jump past the rendered rows): nothing to hold on to.
    anchor.current = end ? { key: "", top: 0, end } : null;
  }, [viewport]);

  /** Recomputes the window from the scroll position. */
  const sync = React.useCallback(() => {
    if (!enabled) return;
    const g = geometry();
    if (!g || g.vp.clientHeight === 0) return;
    const top = g.vp.scrollTop - g.blockTop;
    const ks = live.current.keys;
    const r = rangeFor(offsetsNow(), top, g.vp.clientHeight, overscan);
    const first = ks[r.start];
    const last = ks[r.end - 1];
    const atEnd = r.end === ks.length;
    if (first !== undefined && last !== undefined)
      setWin((w) =>
        w && w.first === first && w.last === last && w.atEnd === atEnd ? w : { first, last, atEnd },
      );
  }, [enabled, geometry, offsetsNow, overscan]);

  // Put the anchored row back where it was on screen (rows above it were added, measured or
  // resized, or windowing just started); then re-window on the next frame, never inside this
  // commit, so a window that moves the anchor settles over frames instead of looping.
  const frame = React.useRef(0);
  React.useLayoutEffect(() => {
    if (!enabled) return;
    const vp = viewport.current;
    const a = anchor.current;
    if (vp && a?.end) {
      // At the end it stays at the end, whatever the rows below measured.
      const bottom = vp.scrollHeight - vp.clientHeight;
      if (vp.scrollTop < bottom - 1) vp.scrollTop = bottom;
    } else if (vp && a) {
      const el = vp.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(a.key)}"]`);
      if (el) {
        const drift = el.getBoundingClientRect().top - vp.getBoundingClientRect().top - a.top;
        if (Math.abs(drift) > 1) vp.scrollTop += drift;
      }
    }
    record();
    if (typeof requestAnimationFrame === "undefined") return;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(sync);
  });
  React.useEffect(() => () => cancelAnimationFrame(frame.current), []);

  React.useEffect(() => {
    const vp = viewport.current;
    if (!vp) return;
    const onScroll = () => {
      record();
      sync();
    };
    vp.addEventListener("scroll", onScroll, { passive: true });
    return () => vp.removeEventListener("scroll", onScroll);
  }, [viewport, sync, record]);

  React.useEffect(() => {
    if (!enabled || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      let changed = false;
      for (const e of entries) {
        const key = elements.current.get(e.target);
        if (key === undefined) continue;
        const h = (e.target as HTMLElement).getBoundingClientRect().height;
        if (h > 0 && Math.abs((heights.current.get(key) ?? -1) - h) > 0.5) {
          heights.current.set(key, h);
          changed = true;
        }
      }
      if (changed) setVersion((v) => v + 1);
    });
    observer.current = ro;
    for (const el of elements.current.keys()) ro.observe(el);
    return () => {
      ro.disconnect();
      observer.current = null;
    };
  }, [enabled]);

  const refs = React.useRef(new Map<string, React.RefCallback<HTMLElement>>());
  const measureRef = React.useCallback((key: string) => {
    let ref = refs.current.get(key);
    if (!ref) {
      let current: HTMLElement | null = null;
      ref = (el: HTMLElement | null) => {
        if (current) {
          observer.current?.unobserve(current);
          elements.current.delete(current);
        }
        current = el;
        if (el) {
          elements.current.set(el, key);
          observer.current?.observe(el);
          const h = el.getBoundingClientRect().height;
          if (h > 0) heights.current.set(key, h);
        }
      };
      refs.current.set(key, ref);
    }
    return ref;
  }, []);
  // Forget refs of rows that left the list.
  React.useEffect(() => {
    const live = new Set(keys);
    for (const k of refs.current.keys()) if (!live.has(k)) refs.current.delete(k);
  }, [keys]);

  const blockRef = React.useCallback((el: HTMLElement | null) => {
    block.current = el;
  }, []);

  const scrollToKey = React.useCallback(
    (key: string) => {
      const i = live.current.keys.indexOf(key);
      if (i < 0) return false;
      const g = geometry();
      if (!g) return false;
      const at = offsetsNow()[i] ?? 0;
      anchor.current = null;
      g.vp.scrollTop = Math.max(0, g.blockTop + at - g.vp.clientHeight / 2);
      record();
      sync();
      return true;
    },
    [geometry, offsetsNow, record, sync],
  );

  const total = offsets[n] as number;
  return {
    range,
    before: enabled ? (offsets[range.start] as number) : 0,
    after: enabled ? total - (offsets[range.end] as number) : 0,
    blockRef,
    measureRef,
    scrollToKey,
  };
}
